import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


import ShingleModal from './components/ShingleModal';
import ComparisonTool from './components/ComparisonTool';
import Footer from '../../components/Footer';
import ShinglePricingSection from '../pricing/components/ShinglePricingSection';

import { openCustomerPortal } from '../../utils/cn';

import { motion } from 'framer-motion';
import { useGoogleStats } from '../../hooks/useGoogleStats';

// Same single-source-of-truth fallback used by the homepage SocialProof,
// TrustBridge, RecentInstallsSection, GoogleReviewsBadge, and the /reviews
// hero. Shown until the Supabase aggregate resolves; keeps the number
// matching site-wide.
const FALLBACK_TOTAL_REVIEWS = 32;
const FALLBACK_OVERALL_RATING = 5.0;
// Manufacturer partnerships count (GAF + Owens Corning + TAMKO + Atlas).
// Static — represents which brands OpenbooQ is certified with, not how
// many products are in the catalog. Bump this if a partnership is added.
const TRUSTED_MANUFACTURERS = 4;

const ShinglesPage = () => {
  // LIVE aggregate from the same Supabase source the homepage SocialProof
  // section + GoogleReviewsBadge + /reviews hero share. Single source of
  // truth — hero rating + review count match site-wide.
  const { stats: liveStats } = useGoogleStats();
  const heroTotalReviews = liveStats?.totalReviews ?? FALLBACK_TOTAL_REVIEWS;
  const heroOverallRating = liveStats?.overallRating ?? FALLBACK_OVERALL_RATING;
  const heroFormattedReviewCount = Math.floor(heroTotalReviews).toLocaleString('en-US');

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [filters, setFilters] = useState({
    brands: [],
    colors: [],
    materials: [],
    features: [],
    priceRange: null
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedShingle, setSelectedShingle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comparisonList, setComparisonList] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  // Mock shingles data
  const shinglesData = [
  {
    id: 1,
    name: "Timberline HDZ",
    brand: "GAF",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17d1dee11-1767659407011.png",
    imageAlt: "Close-up view of GAF Timberline HDZ architectural shingles in charcoal color showing dimensional texture",
    images: [
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1596272810344-d903367cd11b",
    "https://images.unsplash.com/photo-1668084065583-2ac5ad16c667"],

    materialType: "Architectural",
    colors: [
    { name: "Charcoal", hex: "#36454F" },
    { name: "Pewter Gray", hex: "#8C8C8C" },
    { name: "Shakewood", hex: "#8B4513" },
    { name: "Mission Brown", hex: "#654321" }],

    features: ["Algae Resistant", "Wind Resistant", "Class A Fire Rating", "StainGuard Plus"],
    warranty: "Lifetime Limited",
    windRating: "130 mph",
    rating: 4.8,
    reviews: 2847,
    priceRange: { min: 4.25, max: 5.50 },
    isPopular: true,
    description: `The Timberline HDZ shingle features GAF's LayerLock Technology and is designed to provide excellent protection against wind uplift. With enhanced nail holding power and improved sealant, these shingles offer superior performance in extreme weather conditions.`,
    specifications: {
      thickness: "Standard",
      weight: "240 lbs per square",
      fireRating: "Class A",
      impactRating: "Class 3",
      algaeResistance: "10 years",
      windWarranty: "15 years"
    },
    installationRequirements: [
    "Minimum 4/12 roof pitch required",
    "Proper ventilation system needed",
    "Ice and water shield in cold climates",
    "GAF certified contractor recommended"],

    installationTime: "1-2 days",
    warrantyDetails: "Lifetime limited warranty covers manufacturing defects and includes wind resistance up to 130 mph for the first 15 years.",
    materialWarranty: "Lifetime Limited",
    windWarranty: "15 years at 130 mph"
  },
  {
    id: 2,
    name: "Duration Premium",
    brand: "Owens Corning",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18324bb38-1774631438084.png",
    imageAlt: "Owens Corning Duration Premium shingles installed on residential roof showing estate gray color and dimensional profile",
    images: [
    "https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1606150645511-c6d8c0eed36a",
    "https://img.rocket.new/generatedImages/rocket_gen_img_15ed232ae-1766508881751.png"],

    materialType: "Architectural",
    colors: [
    { name: "Estate Gray", hex: "#708090" },
    { name: "Onyx Black", hex: "#0F0F0F" },
    { name: "Driftwood", hex: "#8B7355" },
    { name: "Sedona Canyon", hex: "#A0522D" }],

    features: ["SureNail Technology", "Algae Resistant", "Cool Roof", "Energy Star"],
    warranty: "Limited Lifetime",
    windRating: "130 mph",
    rating: 4.7,
    reviews: 1923,
    priceRange: { min: 4.75, max: 6.25 },
    isPremium: true,
    description: `Duration Premium shingles feature patented SureNail Technology with an engineered fabric strip that enhances nail holding power. These shingles provide superior wind resistance and come with enhanced color options.`,
    specifications: {
      thickness: "Premium",
      weight: "260 lbs per square",
      fireRating: "Class A",
      impactRating: "Class 4",
      algaeResistance: "10 years",
      windWarranty: "15 years"
    },
    installationRequirements: [
    "Minimum 2/12 roof pitch required",
    "SureNail installation method required",
    "Proper attic ventilation essential",
    "Owens Corning certified installer preferred"],

    installationTime: "1-2 days",
    warrantyDetails: "Limited lifetime warranty with SureNail Technology providing enhanced wind resistance and superior performance.",
    materialWarranty: "Limited Lifetime",
    windWarranty: "15 years at 130 mph"
  },
  {
    id: 3,
    name: "Titan XT",
    brand: "TAMKO",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_110b7c499-1776099914036.png",
    imageAlt: "TAMKO Titan XT architectural shingles in rustic hickory color showing dimensional shadow lines and durable construction",
    images: [
    "https://img.rocket.new/generatedImages/rocket_gen_img_1a479ca07-1767552718235.png",
    "https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop"],

    materialType: "Architectural",
    colors: [
    { name: "Rustic Hickory", hex: "#8B7355" },
    { name: "Thunderstorm Grey", hex: "#4F4F4F" },
    { name: "Natural Timber", hex: "#A0522D" },
    { name: "Rustic Black", hex: "#2F2F2F" }],

    features: ["Algae Guard", "Wind Resistant", "Class A Fire Rating", "MetalGuard"],
    warranty: "Limited Lifetime",
    windRating: "130 mph",
    rating: 4.6,
    reviews: 1456,
    priceRange: { min: 3.95, max: 5.25 },
    description: `TAMKO Titan XT shingles deliver outstanding durability with enhanced weather protection. Features MetalGuard protection against metal corrosion and Algae Guard for lasting beauty and performance.`,
    specifications: {
      thickness: "Standard Plus",
      weight: "250 lbs per square",
      fireRating: "Class A",
      impactRating: "Class 3",
      algaeResistance: "10 years",
      windWarranty: "15 years"
    },
    installationRequirements: [
    "Minimum 4/12 roof pitch required",
    "Proper underlayment installation",
    "Adequate roof ventilation",
    "TAMKO certified installer recommended"],

    installationTime: "1-2 days",
    warrantyDetails: "Limited lifetime warranty with MetalGuard and Algae Guard protection systems.",
    materialWarranty: "Limited Lifetime",
    windWarranty: "15 years at 130 mph"
  },
  {
    id: 5,
    name: "Heritage Premium",
    brand: "Tamko",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b10fba73-1774631435091.png",
    imageAlt: "Tamko Heritage Premium shingles in rustic brown showing traditional architectural styling and shadow lines",
    images: [
    "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&h=300&fit=crop",
    "https://img.rocket.new/generatedImages/rocket_gen_img_189f16901-1776092759561.png"],

    materialType: "Architectural",
    colors: [
    { name: "Rustic Brown", hex: "#8B4513" },
    { name: "Weathered Gray", hex: "#696969" },
    { name: "Forest Green", hex: "#228B22" },
    { name: "Autumn Blend", hex: "#D2691E" }],

    features: ["Algae Guard", "Wind Resistant", "Class A Fire Rating", "MetalGuard"],
    warranty: "Limited Lifetime",
    windRating: "110 mph",
    rating: 4.4,
    reviews: 756,
    priceRange: { min: 3.75, max: 4.95 },
    description: `Heritage Premium shingles combine traditional styling with modern performance. Features MetalGuard protection against metal corrosion and Algae Guard for lasting beauty.`,
    specifications: {
      thickness: "Standard",
      weight: "235 lbs per square",
      fireRating: "Class A",
      impactRating: "Class 2",
      algaeResistance: "10 years",
      windWarranty: "10 years"
    },
    installationRequirements: [
    "Minimum 4/12 roof pitch required",
    "MetalGuard installation around metal components",
    "Standard underlayment acceptable",
    "Tamko certified installer recommended"],

    installationTime: "1-2 days",
    warrantyDetails: "Limited lifetime warranty with MetalGuard and Algae Guard protection systems.",
    materialWarranty: "Limited Lifetime",
    windWarranty: "10 years at 110 mph"
  }];


  // Filter and sort shingles
  const filteredAndSortedShingles = useMemo(() => {
    let filtered = shinglesData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter((shingle) =>
      shingle?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      shingle?.brand?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      shingle?.materialType?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      shingle?.colors?.some((color) => color?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
      shingle?.features?.some((feature) => feature?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      );
    }

    // Apply filters
    if (filters?.brands?.length > 0) {
      filtered = filtered?.filter((shingle) => filters?.brands?.includes(shingle?.brand));
    }

    if (filters?.colors?.length > 0) {
      filtered = filtered?.filter((shingle) =>
      shingle?.colors?.some((color) =>
      filters?.colors?.some((filterColor) =>
      color?.name?.toLowerCase()?.includes(filterColor?.toLowerCase())
      )
      )
      );
    }

    if (filters?.materials?.length > 0) {
      filtered = filtered?.filter((shingle) => filters?.materials?.includes(shingle?.materialType));
    }

    if (filters?.features?.length > 0) {
      filtered = filtered?.filter((shingle) =>
      filters?.features?.some((feature) => shingle?.features?.includes(feature))
      );
    }

    if (filters?.priceRange) {
      filtered = filtered?.filter((shingle) =>
      shingle?.priceRange?.min >= filters?.priceRange?.min &&
      shingle?.priceRange?.max <= filters?.priceRange?.max
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered?.sort((a, b) => a?.priceRange?.min - b?.priceRange?.min);
        break;
      case 'price-high':
        filtered?.sort((a, b) => b?.priceRange?.min - a?.priceRange?.min);
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'newest':
        filtered?.sort((a, b) => (b?.isNew ? 1 : 0) - (a?.isNew ? 1 : 0));
        break;
      case 'brand':
        filtered?.sort((a, b) => a?.brand?.localeCompare(b?.brand));
        break;
      case 'popular':
      default:
        filtered?.sort((a, b) => {
          if (a?.isPopular && !b?.isPopular) return -1;
          if (!a?.isPopular && b?.isPopular) return 1;
          return b?.reviews - a?.reviews;
        });
        break;
    }

    return filtered;
  }, [searchTerm, filters, sortBy]);

  const handleViewDetails = (shingle) => {
    setSelectedShingle(shingle);
    setIsModalOpen(true);
  };

  const handleCompare = (shingle) => {
    const isSelected = comparisonList?.some((item) => item?.id === shingle?.id);

    if (isSelected) {
      setComparisonList((prev) => prev?.filter((item) => item?.id !== shingle?.id));
    } else {
      if (comparisonList?.length < 4) {
        setComparisonList((prev) => [...prev, shingle]);
      }
    }
  };

  const handleRemoveFromComparison = (shingle) => {
    setComparisonList((prev) => prev?.filter((item) => item?.id !== shingle?.id));
  };

  const handleClearComparison = () => {
    setComparisonList([]);
  };

  const handleClearFilters = () => {
    setFilters({
      brands: [],
      colors: [],
      materials: [],
      features: [],
      priceRange: null
    });
    setSearchTerm('');
  };

  // Close mobile filter on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsFilterOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Roofing Shingles - Styles, Colors & Durability | OpenbooQ</title>
        <meta name="description" content="Explore our premium selection of high-quality roofing shingles. Compare styles, colors, and durability to find the perfect match for your home's roof." />
        <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Roofing Shingles",
            "description": "Premium roofing shingles from top brands including GAF, Owens Corning, Atlas, and TAMKO",
            "url": "https://openbooq.com/shingles",
            "numberOfItems": shinglesData?.length,
            "itemListElement": shinglesData?.slice(0, 10)?.map((shingle, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": `${shingle?.brand} ${shingle?.name}`,
              "url": "https://openbooq.com/shingles"
            }))
          })}</script>
        <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://openbooq.com" },
            { "@type": "ListItem", "position": 2, "name": "Shingles", "item": "https://openbooq.com/shingles" }]

          })}</script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        {/* ═══ HERO — rebuilt 2026-05-26 from mockup.
            Centered single-column layout (eyebrow pill → 2-line
            headline → subhead → 3 glass stat tiles). Keeps the
            existing residential-home JPG as the background with
            a darker overlay to match the navy mockup tone, plus
            a cyan ambient glow center-top. Stat tiles: product
            count is LIVE from shinglesData.length; manufacturer
            count is the OpenbooQ brand-partnerships static (GAF +
            Owens Corning + TAMKO + Atlas); rating + review count
            are LIVE from useGoogleStats (same source as homepage
            SocialProof / /reviews hero). */}
        <section
          className="relative overflow-hidden pt-48 sm:pt-36 lg:pt-44 pb-20 lg:pb-28"
          style={{
            backgroundImage: `url('/assets/images/pexels-binyaminmellish-186077_2_-1774981506545.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: '#0a1628',
          }}
        >
          {/* Dark navy overlay — matches the warranty / how-it-works
              hero treatment so the white headline + cyan glow stay
              legible over the home photo. */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(14, 30, 54, 0.82) 100%)',
            }}
          />
          {/* Cyan ambient glow center-top */}
          <div
            className="absolute pointer-events-none"
            aria-hidden="true"
            style={{
              top: '15%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              height: '50%',
              background: 'radial-gradient(ellipse, rgba(7,176,233,0.18) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center text-white">
            {/* Eyebrow pill — cyan, "Premium Quality Guaranteed" */}
            <motion.div
              className="mb-7 flex justify-center"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: 'rgba(7, 176, 233, 0.18)',
                  border: '1px solid rgba(7, 176, 233, 0.45)',
                  color: '#6cc7ff',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#10B981' }}
                  aria-hidden="true"
                />
                Premium Quality Guaranteed
              </div>
            </motion.div>

            {/* Two-line headline: "Find Your Perfect" / "Roofing Shingles" */}
            <h1
              className="font-black leading-[1.05] tracking-tight mb-6"
              style={{
                fontSize: 'clamp(40px, 6.5vw, 80px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
              }}
            >
              <motion.span
                className="block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
              >
                Find Your Perfect
              </motion.span>
              <motion.span
                className="block"
                style={{ color: '#07B0E9' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
              >
                Roofing Shingles
              </motion.span>
            </h1>

            {/* Subhead — bold brand names per mockup */}
            <motion.p
              className="mx-auto mb-10 max-w-2xl leading-relaxed"
              style={{
                fontSize: 'clamp(15px, 1.4vw, 18px)',
                color: 'rgba(255, 255, 255, 0.85)',
              }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              Compare premium shingles from{' '}
              <strong className="font-bold text-white">TAMKO, GAF, Owens Corning, and Atlas</strong>. See features, warranties, and transparent per-square pricing — all in one place.
            </motion.p>

            {/* Three glass stat tiles — number + label inline. Stack on
                mobile, row on sm+. */}
            <motion.div
              className="flex flex-wrap justify-center gap-3 sm:gap-4"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            >
              {[
                {
                  number: shinglesData?.length ?? 0,
                  label: 'Premium\nProducts',
                },
                {
                  number: TRUSTED_MANUFACTURERS,
                  label: 'Trusted\nManufacturers',
                },
                {
                  number: heroOverallRating.toFixed(1),
                  label: `Google rating\n· ${heroFormattedReviewCount} reviews`,
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="inline-flex items-center gap-3 rounded-xl px-5 py-3"
                  style={{
                    background: 'rgba(10, 22, 40, 0.55)',
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                  }}
                >
                  <div
                    className="font-black text-white leading-none"
                    style={{
                      fontSize: 'clamp(26px, 2.6vw, 32px)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {stat.number}
                  </div>
                  <div
                    className="text-left text-[12px] sm:text-[13px] leading-snug whitespace-pre-line"
                    style={{ color: 'rgba(255, 255, 255, 0.70)' }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>
        {/* Explore Shingle Options Section - Copied from Pricing Page */}
        <ShinglePricingSection />
        {/* CTA Section */}
        <section className="bg-gradient-to-r from-primary to-secondary py-16 mt-16">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get a quote for your roofing project in 30 minutes with transparent pricing and no hidden fees.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => openCustomerPortal()}>

                Get Your Quote in 30 Minutes
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary"
                onClick={() => window.location.href = '/support'}>

                Talk to Expert
              </Button>
            </div>
          </div>
        </section>
        {/* Modals */}
        <ShingleModal
          shingle={selectedShingle}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedShingle(null);
          }} />
        <ComparisonTool
          selectedShingles={comparisonList}
          onRemove={handleRemoveFromComparison}
          onClear={handleClearComparison}
          isOpen={isComparisonOpen}
          onClose={() => setIsComparisonOpen(false)} />
        {/* Estimate Wizard Modal */}
        {/* Mobile Bottom Padding */}
        <div className="h-20 lg:hidden" />

        {/* ═══ BUYER'S GUIDE — "Choosing the Right Roofing Shingles
            for Your Home". Replaced the legacy SEO body section
            2026-05-26 from the user's mockup. Centered eyebrow +
            headline + 3 paragraphs. Drops the prior 3-column grid
            (Architectural / Impact-Resistant / Designer) — the
            architectural-shingles content now lives in the 3rd
            paragraph instead. */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
            {/* Eyebrow pill */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="mb-6 flex justify-center"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: 'rgba(7, 176, 233, 0.10)',
                  border: '1px solid rgba(7, 176, 233, 0.30)',
                  color: '#0a6eb8',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#07B0E9' }}
                  aria-hidden="true"
                />
                Buyer's Guide
              </div>
            </motion.div>

            {/* Headline */}
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="font-black leading-[1.1] tracking-tight mb-6 mx-auto"
              style={{
                fontSize: 'clamp(28px, 3.6vw, 44px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.02em',
                color: '#0a1628',
              }}
            >
              Choosing the Right Roofing Shingles for Your Home
            </motion.h2>

            {/* 3 paragraphs */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-6 text-base lg:text-[17px] text-gray-600 leading-relaxed"
            >
              <p>
                Selecting the right shingles for your roof replacement is one of the most important decisions you'll make as a homeowner. The right product balances aesthetics, durability, warranty coverage, and budget — and the best choice depends on your home's architecture, your local climate, and your long-term plans for the property.
              </p>
              <p>
                Your local climate presents specific challenges for roofing materials. Harsh winters with ice and snow, spring hailstorms, and hot humid summers all stress asphalt shingles differently. That's why OpenbooQ Inc. curates only proven, high-performance products from the industry's most trusted manufacturers — <strong className="font-bold text-gray-900">TAMKO, GAF, Owens Corning, and Atlas</strong>.
              </p>
              <p>
                For most homes, <strong className="font-bold text-gray-900">architectural (dimensional) shingles</strong> are the most popular choice — they offer a dimensional, wood-shake appearance with superior durability over 3-tab shingles. Architectural shingles feature enhanced wind resistance, algae protection, and lifetime limited warranties, making them an excellent value for most residential applications.
              </p>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>);











};

export default ShinglesPage;